/**
 * l3ns tracking SDK — public API + script-tag auto-init.
 *
 * IIFE build: `window.l3ns.{init,track,tag,set,funnel,error,consent,flush,diag}`.
 * ESM build: named exports of the same API.
 * Every entry point is wrapped in safe() — errors never reach the host page.
 */
import { CONFIG_PATH } from '@l3ns/shared/constants';
import type { SdkInitOptions, SdkRemoteConfig } from '@l3ns/shared/types';
import { getTimeOnPage, initActivity } from './auto/activity';
import { errProps, initErrors } from './auto/errors';
import { initPageviews } from './auto/pageviews';
import { initScroll } from './auto/scroll';
import { runSnippets } from './auto/snippets';
import { idle } from './auto/util';
import { loadX } from './auto/xapi';
import { initHints } from './core/context';
import { dlog, safe } from './core/safe';
import { ensureSession } from './core/session';
import { st, VERSION } from './core/state';
import { enqueue } from './core/track';
import { flush as flushNow, startTransport } from './core/transport';
import { setUser } from './core/user';

function fetchRemoteConfig(): void {
	// non-critical: fetch when the main thread is idle
	idle(
		safe(() => {
			void fetch(st.api_ + CONFIG_PATH + '?key=' + encodeURIComponent(st.key_))
				.then((r) => (r.ok ? (r.json() as Promise<SdkRemoteConfig>) : null))
				.then((cfg) => {
					if (cfg?.v == 1) {
						st.auto_ = { ...st.auto_, ...cfg.autoTrack, ...st.localAuto_ };
						st.privacy_ = { ...st.privacy_, ...cfg.privacy, ...st.localPrivacy_ };
						st.mappings_ = cfg.mappings || [];
						st.formSteps_ = cfg.formSteps || [];
						st.zones_ = cfg.zones || [];
						st.bridges_ = cfg.bridges || [];
						st.identity_ = cfg.identity;
						dlog('cfg', cfg);
						// On Load tag rules: one l3ns_loaded per page load once the SDK is
						// fully up — on idle, so it never competes with the host page
						if (st.auto_.onLoad)
							idle(safe(() => enqueue('l3ns_loaded', { ms: Math.round(performance.now()) })));
						// …and their tags' HTML snippets, each on its own idle slot
						if (cfg.snippets) runSnippets(cfg.snippets);
					}
				})
				.catch(() => {})
				// settled either way — buffered-capture modules (net) may proceed
				.finally(() => {
					st.cfg_ = true;
				});
		}),
	);
}

function initImpl(opts: SdkInitOptions): void {
	if (st.inited_ || !opts || typeof opts.projectKey != 'string' || opts.projectKey.length < 8)
		return;
	const t0 = performance.now();
	st.key_ = opts.projectKey;
	st.api_ = (opts.apiHost ?? location.origin).replace(/\/+$/, '');
	st.env_ = opts.environment ?? 'production';
	st.rel_ = opts.release ?? '';
	st.debug_ = opts.debug === true;
	st.consent_ = opts.consent !== false;
	Object.assign(st.auto_, (st.localAuto_ = opts.autoTrack || {}));
	Object.assign(st.privacy_, (st.localPrivacy_ = opts.privacy || {}));
	if (opts.flushIntervalMs && opts.flushIntervalMs >= 500) st.flushMs_ = opts.flushIntervalMs;
	st.inited_ = true;

	const s = ensureSession();
	// real OS version (UA client hints) lands async — before the first flush
	initHints();
	if (st.auto_.sessions && s.startedNew) {
		enqueue('session_started');
		if (s.isReturning) enqueue('user_returned');
	}
	// immediate: pageviews (first page_viewed, pre-flush hide hooks) + errors —
	// both must not miss early activity; the rest attaches when idle
	safe(initPageviews)();
	safe(initErrors)();

	startTransport();
	idle(() => {
		for (const f of [initActivity, initScroll, loadX]) safe(f)();
	});
	fetchRemoteConfig();
	st.diag_.initMs = Math.round((performance.now() - t0) * 100) / 100;
	dlog('init', VERSION, st.key_, st.api_, st.diag_.initMs);
}

export const init = safe(initImpl);
export const track = safe(enqueue);
export const tag = safe((name: string) => enqueue('tag_applied', { tag: String(name) }));
// project variables (sdk-sourced): resolved server-side onto the session
export const set = safe((key: string, value: string | number | boolean) =>
	enqueue('variable_set', { key: String(key), value: String(value) }),
);
const fun = (n: string) =>
	safe((f: string, step?: string) =>
		enqueue('funnel_' + n, { funnel: String(f), ...(step != undefined && { step: String(step) }) }),
	);
export const funnel = { start: fun('started'), step: fun('step'), complete: fun('completed') };
// signed-in user for this tab's sessions; identify() alone signs out
export const identify = safe(setUser);
export const error = {
	capture: safe((err: unknown, ctx?: Record<string, unknown>) =>
		enqueue('error_captured', { ...errProps(err), manual: true, ...ctx }),
	),
};
export const consent = safe((granted: boolean) => {
	st.consent_ = granted !== false;
});
export const flush = safe(() => flushNow('manual'));
export const diag = safe(() => ({
	...st.diag_,
	queued: st.q_.length,
	timeOnPage: getTimeOnPage(),
	version: VERSION,
}));
export const version = VERSION;

// Explicit global for the script-tag build — esbuild's `var l3ns=` IIFE
// assignment is dead-code to terser (nothing in-file reads it) and gets
// dropped, so we install the API object ourselves. Idempotent.
safe(() => {
	const g = globalThis as { l3ns?: unknown };
	if (!g.l3ns)
		g.l3ns = { init, track, tag, set, identify, funnel, error, consent, flush, diag, version };
})();

// --- script-tag auto-init ----------------------------------------------------
safe(() => {
	const el = document.currentScript as HTMLScriptElement | null;
	const g = (n: string) => el!.getAttribute('data-' + n);
	const key = el && g('project-key');
	if (!key) return;
	let apiHost = g('api-host') ?? undefined;
	if (!apiHost && el!.src)
		try {
			apiHost = new URL(el!.src, location.href).origin;
		} catch {}
	initImpl({
		projectKey: key,
		apiHost,
		environment: g('environment') ?? undefined,
		release: g('release') ?? undefined,
		debug: g('debug') != null,
		flushIntervalMs: Number(g('flush-ms')) || undefined,
	});
})();
